import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Fade,
  Stack,
  Chip,
  Card,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  LinkOff as LinkOffIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import AddFieldDialog from "./AddFieldDialog";

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 330;

const getFieldSelectionKey = (field) =>
  field?.field_instance_id ??
  field?.id ??
  field?.name ??
  field?.field_name ??
  field?.row ??
  `${field?.field_label ?? field?.label ?? "field"}_${field?.page ?? ""}`;

const AddFieldDrawer = ({
  isOpen,
  currentProcessingFile,
  setCurrentProcessingFile,
  currCursor,
  formFields,
  onToggleObjectInspector,
}) => {
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const [openUnassignFieldDialog, setOpenUnassignFieldDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const getFormField = useCallback(
    (id) => formFields?.find((f) => f?.field_id === id),
    [formFields],
  );

  const handleCloseAddFieldDialog = () => {
    setOpenAddFieldDialog(false);
  };

  const handleCloseUnassignFieldDialog = () => {
    setOpenUnassignFieldDialog(false);
    setSelectedField(null);
  };

  const onMouseDown = useCallback(
    (e) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (e) => {
        if (!isResizing.current) return;
        const delta = startX.current - e.clientX;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, startWidth.current + delta),
        );
        setWidth(newWidth);
      };

      const onMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  const handleUnassign = (isBlock, isField) => {
    if (isField && isBlock) {
      setCurrentProcessingFile((prevFile) => {
        const tempFields = prevFile?.document?.tempFields;
        const updatedFormFields = tempFields?.formFields?.map((f) => {
          if (selectedField?.name === f?.name) {
            return {
              ...f,
              field_id: "",
              location: "",
              block_id: "",
              block_label: "",
            };
          } else {
            return f;
          }
        });

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updatedFormFields,
            },
          },
        };
      });
      handleCloseUnassignFieldDialog();
      return;
    }

    if (isField) {
      setCurrentProcessingFile((prevFile) => {
        const tempFields = prevFile?.document?.tempFields;
        const updatedFormFields = tempFields?.formFields?.map((f) => {
          if (selectedField?.name === f?.name) {
            return {
              ...f,
              field_id: "",
              location: "",
            };
          } else {
            return f;
          }
        });

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updatedFormFields,
            },
          },
        };
      });
      handleCloseUnassignFieldDialog();
      return;
    }

    if (isBlock) {
      const tempFields = currentProcessingFile?.document?.tempFields;

      const updatedFormFields = tempFields?.formFields?.map((f) => {
        if (selectedField?.name === f?.name) {
          return {
            ...f,
            block_id: "",
            block_label: "",
          };
        } else {
          return f;
        }
      });

      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: updatedFormFields,
          },
        },
      }));
      handleCloseUnassignFieldDialog();
      return;
    }
  };

  const handleSelectChange = useCallback(
    (e, field, isEditClick) => {
      e.stopPropagation();

      if (currCursor === "crosshair") return;

      setCurrentProcessingFile((prevFile) => {
        const fieldSelectionKey = getFieldSelectionKey(field);
        const isAlreadySelected =
          prevFile.document.tempFields.selectedExtraFields.some(
            (f) => getFieldSelectionKey(f) === fieldSelectionKey,
          );

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedExtraFields: isAlreadySelected
                ? isEditClick
                  ? prevFile.document.tempFields.selectedExtraFields
                  : prevFile.document.tempFields.selectedExtraFields.filter(
                      (f) => getFieldSelectionKey(f) !== fieldSelectionKey,
                    )
                : [...prevFile.document.tempFields.selectedExtraFields, field],

              selectedPdfTextItem: null,
              selectedExtraPdfTextItems: [],
              selectedFormField: { ...field },
            },
          },
        };
      });
    },
    [currCursor, setCurrentProcessingFile],
  );

  const extraFormFields = useMemo(
    () =>
      currentProcessingFile?.document?.tempFields?.formFields?.filter((f) => {
        const formField = getFormField(f?.field_id);

        return (
          (formField?.field_type === "percentageCheckbox" || f?.hidden) &&
          f?.type !== "PDFRectangle"
        );
      }) ?? [],
    [currentProcessingFile, getFormField],
  );

  const selectedFormFields = useMemo(
    () =>
      currentProcessingFile?.document?.tempFields?.selectedExtraFields?.map(
        (f) => getFieldSelectionKey(f),
      ) ?? [],
    [currentProcessingFile],
  );

  return (
    <>
      <Fade in>
        <Box sx={{ display: "flex", position: "relative" }}>
          {isOpen && (
            <Box
              onMouseDown={onMouseDown}
              sx={{
                width: 5,
                cursor: "col-resize",
                bgcolor: "transparent",
                borderRight: "1px solid #E5E7EB",
                flexShrink: 0,
                transition: "background-color 0.15s",
                "&:hover": {
                  bgcolor: "primary.main",
                  opacity: 0.4,
                },
                "&:active": {
                  bgcolor: "primary.main",
                  opacity: 0.6,
                },
              }}
            />
          )}
          <Box
            sx={{
              width: isOpen ? width : 0,
              minWidth: isOpen ? MIN_WIDTH : 0,
              maxWidth: isOpen ? MAX_WIDTH : 0,
              height: "100%",
              transition: "all 260ms ease",
              overflow: "hidden",
              borderLeft: isOpen ? "1px solid #e0e0e0" : "none",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
              <Typography
                variant="subtitle1"
                align="center"
                sx={{ fontWeight: 700 }}
              >
                Extra Form Fields
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                px: 1.5,
                py: 1.25,
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setOpenAddFieldDialog(true);
                }}
                color="primary"
              >
                Add Extra Form Field
              </Button>
            </Box>

            {extraFormFields.length === 0 ? (
              <Box sx={{ px: 1.5 }}>
                <Typography
                  color="primary"
                  variant="h6"
                  mb={2}
                  mt={2.5}
                  sx={{ fontSize: "1rem" }}
                  align="center"
                >
                  You have no fields to display just yet
                </Typography>
              </Box>
            ) : (
              <>
                <Box flex={1} sx={{ px: 1.5, overflow: "auto" }}>
                  <Stack gap={0.75}>
                    {extraFormFields?.map((field, index) => {
                      const formField = getFormField(field?.field_id);
                      const fieldSelectionKey = getFieldSelectionKey(field);
                      return (
                        <Card
                          sx={{
                            p: 1.5,
                            pt: "2px !important",
                            boxShadow: "none !important",
                            "--Paper-shadow": "none !important",
                            borderRadius: "12px !important",
                            border: "1px solid #e0e0e0",
                            borderLeft: "3px solid #1976d2",
                          }}
                          key={fieldSelectionKey || index}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Box>
                              <Box
                                display={"flex"}
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "rgba(0, 0, 0, 0.8)",
                                    fontWeight: 500,
                                  }}
                                >
                                  {field?.field_label ?? field?.label}
                                </Typography>
                                <Box
                                  display={"flex"}
                                  alignItems="center"
                                  gap={1}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      handleSelectChange(e, field, true);
                                      onToggleObjectInspector();
                                    }}
                                    sx={{
                                      border: "1px solid #d0d7de",
                                      borderRadius: 1.5,
                                      backgroundColor: "transparent",
                                      p: "4px !important",
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                  {(field?.block_id || field?.field_id) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setOpenUnassignFieldDialog(true);
                                        setSelectedField(field);
                                      }}
                                      sx={{
                                        border: "1px solid #d0d7de",
                                        borderRadius: 1.5,
                                        backgroundColor: "transparent",
                                        p: "4px !important",
                                      }}
                                    >
                                      <LinkOffIcon sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  )}
                                  <Checkbox
                                    size="small"
                                    checked={selectedFormFields?.includes(
                                      fieldSelectionKey,
                                    )}
                                    onChange={(e) =>
                                      handleSelectChange(e, field)
                                    }
                                  />
                                </Box>
                              </Box>
                              <Box
                                display={"flex"}
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "rgba(0, 0, 0, 0.8)",
                                    fontWeight: 400,
                                  }}
                                >
                                  {field?.name ?? field?.field_name}
                                </Typography>
                                {(field?.type || field?.field_type) && (
                                  <Chip
                                    label={field?.type ?? field?.field_type}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                      fontSize: "0.7rem",
                                      height: 20,
                                      borderRadius: 1,
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "rgba(0, 0, 0, 0.8)",
                                  fontWeight: 400,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  component={"span"}
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "rgba(0, 0, 0, 0.8)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Assigned Field:{" "}
                                </Typography>
                                {formField?.field_label || "N/A"}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.75rem",
                                  color: "rgba(0, 0, 0, 0.8)",
                                  fontWeight: 400,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  component={"span"}
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "rgba(0, 0, 0, 0.8)",
                                    fontWeight: 500,
                                  }}
                                >
                                  Block:{" "}
                                </Typography>
                                {`${field?.block_label || "N/A"}`}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Fade>
      {openAddFieldDialog && (
        <AddFieldDialog
          open={openAddFieldDialog}
          handleClose={handleCloseAddFieldDialog}
          currentProcessingFile={currentProcessingFile}
          setCurrentProcessingFile={setCurrentProcessingFile}
        />
      )}
      <Dialog
        open={openUnassignFieldDialog}
        onClose={handleCloseUnassignFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unassign Field</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to unassign field?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Box>
            {selectedField?.block_id && selectedField?.field_id && (
              <Button
                onClick={() => handleUnassign(true, true)}
                variant="contained"
                sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
              >
                Unassign Both
              </Button>
            )}
          </Box>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Button onClick={handleCloseUnassignFieldDialog} variant="outlined">
              Cancel
            </Button>
            {selectedField?.block_id && (
              <Button
                onClick={() => handleUnassign(true, false)}
                variant="contained"
                color="error"
              >
                Unassign Block
              </Button>
            )}
            {selectedField?.field_id && (
              <Button
                onClick={() => handleUnassign(false, true)}
                variant="contained"
                color="error"
              >
                Unassign Field
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddFieldDrawer;
